import { computed } from 'vue'
import { useStore } from 'vuex'
import axios from '@/libs/axios'

export const PHONE_REGEX = /^\+[1-9]\d{5,14}$/

export const normalizeDialCode = (code) => String(code ?? '').replace(/\D/g, '')

export const formatDialOptionText = (shortName, code) => {
  const label = String(shortName || '').trim().toUpperCase()
  return label ? `${label} +${code}` : `+${code}`
}

export const extractCountries = (payload) => {
  if (Array.isArray(payload)) {
    return payload
  }
  if (Array.isArray(payload?.data)) {
    return payload.data
  }
  return []
}

export const buildPhoneDialOptions = (countries) => {
  const options = extractCountries(countries)
      .map((country) => {
        const code = normalizeDialCode(country.phone_code)
        if (!code || code === '0') {
          return null
        }

        const shortName = String(country.short_name || '').trim().toUpperCase()

        return {
          id: country.id,
          value: code,
          shortName,
          text: formatDialOptionText(shortName, code),
        }
      })
      .filter(Boolean)

  if (!options.length) {
    const fallback = normalizeDialCode(window.companyPhoneCode) || '1'
    options.push({
      id: 'fallback',
      value: fallback,
      shortName: String(window.companyCountryCode || '').trim().toUpperCase(),
      text: formatDialOptionText(window.companyCountryCode, fallback),
    })
  }

  return options.sort((a, b) => a.shortName.localeCompare(b.shortName))
}

export const getDefaultDialCode = (countries) => {
  const option = getDefaultDialOption(countries)
  return option?.value || '1'
}

export const getDefaultDialOption = (countries, preferredCountryId = null) => {
  const options = buildPhoneDialOptions(countries)
  if (!options.length) {
    return null
  }

  const preferredId = preferredCountryId ?? window.companyCountryId
  if (preferredId != null && preferredId !== '') {
    const byId = options.find((option) => String(option.id) === String(preferredId))
    if (byId) {
      return byId
    }
  }

  const shortName = String(window.companyCountryCode || '').trim().toUpperCase()
  if (shortName) {
    const byShortName = options.find((option) => option.shortName === shortName)
    if (byShortName) {
      return byShortName
    }
  }

  const dial = normalizeDialCode(window.companyPhoneCode)
  if (dial && dial !== '0') {
    const byDial = resolveDialOption(options, dial, preferredId)
    if (byDial) {
      return byDial
    }
  }

  return options[0]
}

/**
 * Pick the country option for a dial code, preferring company / preferred country
 * when several countries share the same code (e.g. US/CA +1).
 */
export const resolveDialOption = (dialOptions, dialCode, preferredCountryId = null) => {
  const options = dialOptions || []
  const code = String(dialCode || '')
  if (!code) {
    return null
  }

  const matches = options.filter((option) => String(option.value) === code)
  if (!matches.length) {
    return null
  }

  const preferredId = preferredCountryId ?? window.companyCountryId
  if (preferredId != null && preferredId !== '') {
    const byId = matches.find((option) => String(option.id) === String(preferredId))
    if (byId) {
      return byId
    }
  }

  const shortName = String(window.companyCountryCode || '').trim().toUpperCase()
  if (shortName) {
    const byShortName = matches.find((option) => option.shortName === shortName)
    if (byShortName) {
      return byShortName
    }
  }

  return matches[0]
}

export const buildFullPhone = (dialCode, nationalNumber) => {
  const dial = normalizeDialCode(dialCode)
  const national = String(nationalNumber || '').replace(/\D/g, '')
  if (!dial && !national) {
    return ''
  }
  return `+${dial}${national}`
}

export const isValidFullPhone = (value) => (
  PHONE_REGEX.test(String(value || '').replace(/[^\d+]/g, ''))
)

export const parseFullPhone = (phone, dialOptions, defaultDialCode, preferredCountryId = null) => {
  const digits = String(phone || '').replace(/\D/g, '')
  const fallbackDial = typeof defaultDialCode === 'function'
      ? defaultDialCode()
      : defaultDialCode

  if (!digits) {
    const fallbackOption = resolveDialOption(dialOptions, fallbackDial, preferredCountryId)
    return {
      dialCode: fallbackOption?.value || fallbackDial,
      countryId: fallbackOption?.id ?? null,
      national: '',
    }
  }

  const dialCodes = [...new Set((dialOptions || []).map((option) => option.value))]
      .sort((a, b) => b.length - a.length)

  for (const dialCode of dialCodes) {
    if (digits.startsWith(dialCode)) {
      const option = resolveDialOption(dialOptions, dialCode, preferredCountryId)
      return {
        dialCode,
        countryId: option?.id ?? null,
        national: digits.slice(dialCode.length),
      }
    }
  }

  const fallbackOption = resolveDialOption(dialOptions, fallbackDial, preferredCountryId)
  return {
    dialCode: fallbackOption?.value || fallbackDial,
    countryId: fallbackOption?.id ?? null,
    national: digits,
  }
}

export const ensureDialInOptions = (dialRef, dialOptions, defaultDialCode, preferredCountryId = null) => {
  const options = dialOptions || []
  if (!options.length) {
    return
  }

  const resolveDefault = () => {
    if (typeof defaultDialCode === 'function') {
      return defaultDialCode()
    }
    return defaultDialCode
  }

  if (!options.some((option) => option.value === String(dialRef.value))) {
    dialRef.value = resolveDefault()
  }

  // Ensure callers can also learn which country label to show for shared dial codes.
  return resolveDialOption(options, dialRef.value, preferredCountryId)
}

export function usePhoneCountries() {
  const store = useStore()

  const phoneCountries = computed(() => extractCountries(store.state.companies.countries))

  const phoneDialOptions = computed(() => buildPhoneDialOptions(phoneCountries.value))

  const defaultDialCode = () => getDefaultDialCode(phoneCountries.value)

  const defaultDialOption = (preferredCountryId = null) => (
    getDefaultDialOption(phoneCountries.value, preferredCountryId)
  )

  const loadPhoneCountries = async () => {
    if (phoneCountries.value.length) {
      return phoneCountries.value
    }

    try {
      const data = await store.dispatch('companies/loadCountries')
      return extractCountries(data)
    } catch (error) {
      try {
        const response = await axios.get('api/countries')
        store.commit('companies/SET_COUNTRIES', extractCountries(response.data))
        return extractCountries(response.data)
      } catch (innerError) {
        return phoneCountries.value
      }
    }
  }

  return {
    phoneCountries,
    phoneDialOptions,
    defaultDialCode,
    defaultDialOption,
    loadPhoneCountries,
  }
}
